import { useEffect, useRef } from 'react'

import { faSearch } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { T, Tooltip } from 'govwell-ui'
import { twMerge } from 'tailwind-merge'

import { routes, useLocation } from '@redwoodjs/router'

import CityFooterDisplay from 'src/components/Sidebar/CityFooterDisplay'
import HelpNavItem from 'src/components/Sidebar/HelpNavItem'
import Navigation, { NavGroupKey } from 'src/components/Sidebar/Navigation/Navigation'
import SettingsNavItem from 'src/components/Sidebar/StaffSidebar/SettingsNavItem'
import { useStaffSidebarMainNavElements } from 'src/components/Sidebar/StaffSidebar/use-staff-sidebar-main-nav-elements'
import UserProfileNavItem from 'src/components/Sidebar/UserProfileNavItem'
import Text from 'src/components/Typography/Text'
import { KeyboardKey } from 'src/types'
import { LayoutMode } from 'src/utils/layout'
import { getModifierKey } from 'src/utils/window'

import useDisclosure from '../../../hooks/use-disclosure'
import {
  useAmIAIStaffMember,
  useMyOrg,
  useMyRecordTypes,
} from '../../../layouts/AppStateContextLayout/utils'
import UniversalSearchModal from '../../UniversalSearchModal/UniversalSearchModal'
import { SidebarPrimitive } from '../SidebarPrimitive'

type Props = {
  isNavGroupOpenMap: Map<string, boolean>
  layoutMode: LayoutMode
  onToggleNavGroupIsOpen: (key: string) => void
}
export const StaffSidebar = ({ isNavGroupOpenMap, layoutMode, onToggleNavGroupIsOpen }: Props) => {
  const { pathname } = useLocation()

  const isAIAssistantActive = pathname.includes(routes.aiAssistantSettings())
  const recordTypes = useMyRecordTypes()

  const hasCodeEnforcementModule = useMyOrg()?.hasCodeEnforcementModule
  const hasRecordTypes = !!recordTypes?.length
  const isAIStaffMember = useAmIAIStaffMember()
  const searchModalState = useDisclosure()
  const searchButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === KeyboardKey.K) {
        e.preventDefault()
        searchModalState.open()
      }
    }
    window.addEventListener('keydown', listener)
    return () => window.removeEventListener('keydown', listener)
  }, [searchModalState])

  const modifierKey = getModifierKey()

  // When the user goes to the Community Assistant page and isn't part of the AI team, expand the nav items on the left by default
  useEffect(() => {
    if (
      isAIAssistantActive &&
      !isAIStaffMember &&
      isNavGroupOpenMap.get(NavGroupKey.AIAssistant) === undefined
    ) {
      onToggleNavGroupIsOpen(NavGroupKey.AIAssistant)
    }
  }, [isAIAssistantActive, isAIStaffMember, isNavGroupOpenMap, onToggleNavGroupIsOpen])

  const mainNavElements = useStaffSidebarMainNavElements()
  const isCollapsed = layoutMode === LayoutMode.SidebarCollapsed

  return (
    <SidebarPrimitive layoutMode={layoutMode}>
      <SidebarPrimitive.Header>
        {(hasCodeEnforcementModule || hasRecordTypes) && (
          <Tooltip content={isCollapsed ? 'Search' : undefined} side="right">
            <button
              onClick={searchModalState.open}
              ref={searchButtonRef}
              className={twMerge(
                `!flex h-8 w-full cursor-pointer items-center justify-between gap-3 rounded-lg border border-neutral-300 bg-neutral-200 px-3 py-1 text-neutral-950 focus-visible:!outline-1 focus-visible:!outline-neutral-950 dark:border-none dark:bg-neutral-600 dark:text-neutral-50 dark:focus-visible:!outline-neutral-50`,
                isCollapsed ? 'p-[9px]' : ''
              )}
              style={{
                maxWidth: isCollapsed ? '32px' : 'auto',
                minWidth: isCollapsed ? '32px' : 'auto',
              }}
            >
              <T.Body className="text-inherit">
                <FontAwesomeIcon icon={faSearch} />
              </T.Body>
              {!isCollapsed && (
                <>
                  <T.Body className="flex-1 text-left text-inherit">Search</T.Body>
                  {modifierKey && <Text className="!text-neutral-400">{`${modifierKey}+K`}</Text>}
                </>
              )}
            </button>
          </Tooltip>
        )}
        {searchModalState.isOpen && (
          <UniversalSearchModal modalState={searchModalState} onCloseFocusRef={searchButtonRef} />
        )}
      </SidebarPrimitive.Header>
      <SidebarPrimitive.Content>
        <Navigation
          isNavGroupOpenMap={isNavGroupOpenMap}
          elements={mainNavElements}
          onToggleIsOpen={onToggleNavGroupIsOpen}
        />
      </SidebarPrimitive.Content>
      <SidebarPrimitive.Footer>
        {!isCollapsed && <CityFooterDisplay />}
        <UserProfileNavItem />
        <HelpNavItem
          isNavGroupOpenMap={isNavGroupOpenMap}
          onToggleNavGroupIsOpen={onToggleNavGroupIsOpen}
        />
        <SettingsNavItem />
      </SidebarPrimitive.Footer>
    </SidebarPrimitive>
  )
}
